import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TextField, MenuItem } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../app/store';
import { setEncounterAction as setEncounter } from '../../../../services/EncounterService/slice';
import { useGetPhysiciansQuery } from '../../../../services/ConfigurationService';
import { EncounterType } from '../../../../services/EncounterService/types/encounterTypes';

const PhysicianSelectEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const dispatch = useAppDispatch();
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const [selectedPhysicianId, setSelectedPhysicianId] = useState<string>('');

  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'CANCEL';

  // Fetch physicians for the center
  const { data: physicians = [], isLoading: physiciansLoading } = useGetPhysiciansQuery(
    { company: company, center: `${company}-${center}` },
    {
      skip: !company || !center,
    }
  );

  // Initialize selected physician from encounter state when data loads
  useEffect(() => {
    if (encounter?.physician?.id) {
      setSelectedPhysicianId(encounter.physician.id);
    }
  }, [encounter?.physician?.id]);

  const handlePhysicianChange = (physicianId: string) => {
    if (!encounter) return;
    
    setSelectedPhysicianId(physicianId);
    const selectedPhysician = physicians.find(p => p.id === physicianId);
    if (selectedPhysician) {
      const updated = {
        ...encounter,
        physician: {
          ...encounter.physician,
          id: selectedPhysician.id || '',
          firstName: selectedPhysician.firstName || '',
          lastName: selectedPhysician.lastName || '',
          npi: selectedPhysician.npi || '',
        },
      } as EncounterType;
      dispatch(setEncounter(updated));
    }
  };

  return (
    <TextField
      label="Select Physician"
      size="small"
      select
      value={selectedPhysicianId}
      onChange={(e) => handlePhysicianChange(e.target.value)}
      disabled={physiciansLoading || isEncounterCancelled}
      sx={{ 
        flex: 2,
        '& .MuiInputBase-input': {
          backgroundColor: isEncounterCancelled ? '#f5f5f5' : 'inherit'
        }
      }}
      helperText={physiciansLoading ? 'Loading physicians...' : isEncounterCancelled ? 'Read-only (Encounter Cancelled)' : ''}
    >
      {physicians.map((physician) => (
        <MenuItem key={physician.id} value={physician.id}>
          {`${physician.firstName} ${physician.lastName} - NPI: ${physician.npi}`}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default PhysicianSelectEdit;
